import React, { ReactNode } from "react";
import classNames from "classnames";
import { FormikErrors, FormikTouched, getIn } from "formik";
import { twMerge } from "tailwind-merge";
import { FaTimes } from "react-icons/fa";

interface DateFieldProps<T> {
  id: string;
  label?: string; // ✅ Added label prop
  value: string; // stored as string: "YYYY-MM-DD"
  onChange: (e: React.ChangeEvent<HTMLInputElement>) => void;
  onBlur?: (e: React.ChangeEvent<HTMLInputElement>) => void;
  onClear?: () => void;
  touched?: FormikTouched<T>;
  errors?: FormikErrors<T>;
  placeholder?: string;
  disabled?: boolean;
  min?: string; // optional min date (YYYY-MM-DD)
  max?: string; // optional max date (YYYY-MM-DD)
}

const DateField: React.FC<DateFieldProps<unknown>> = ({
  id,
  label, // ✅
  value,
  onChange,
  onBlur,
  onClear,
  touched = {},
  errors = {},
  placeholder,
  disabled = false,
  min,
  max,
}) => {
  const inputContainerClasses = twMerge(
    classNames(
      "w-full text-cDarkBlue relative flex flex-col border border-secondary rounded shadow p-2",
      {
        "border-red-700": getIn(errors, id) && getIn(touched, id),
        "opacity-50 cursor-not-allowed": disabled,
      }
    )
  );

  const inputClasses = classNames(
    "w-full min-h-12 p-2 text-gray-700 focus:outline-none border rounded"
  );

  const errorMessageClasses = classNames("text-sm text-red-700 mt-1");

  return (
    <div className={inputContainerClasses}>
      <div className="flex justify-between">
        {label && (
          <label
            htmlFor={id}
            className="text-sm font-medium text-gray-700 mb-1"
          >
            {label}
          </label>
        )}
        {value && (
          <FaTimes
            className="ml-auto text-gray-500 hover:text-gray-700 cursor-pointer"
            onClick={onClear}
            title="Effacer la date"
          />
        )}
      </div>

      <div className="relative">
        <input
          id={id}
          type="date"
          value={value}
          onChange={onChange}
          onBlur={onBlur}
          placeholder={placeholder}
          className={inputClasses}
          disabled={disabled}
          min={min}
          max={max}
        />
      </div>

      {getIn(errors, id) && getIn(touched, id) && (
        <div className={errorMessageClasses}>
          {getIn(errors, id) as ReactNode}
        </div>
      )}
    </div>
  );
};

export default DateField;
